import { Controller } from "@hotwired/stimulus";
import { api } from "../composables/useApi";

let editorModulesPromise = null

function loadEditorModules() {
  editorModulesPromise ||= Promise.all([
    import("@tiptap/core"),
    import("@tiptap/starter-kit"),
    import("@tiptap/extension-link"),
    import("@tiptap/extension-heading"),
    import("~/editor-extensions/figure-image"),
    import("~/editor-extensions/callout"),
    import("@gocapsule/column-extension"),
    import("@tiptap/extension-list-item"),
    import("@tiptap/extension-ordered-list"),
    import("@tiptap/extension-bullet-list"),
    import("@tiptap/extension-text-align"),
    import("@tiptap/extension-table"),
    import("@tiptap/extension-table-row"),
    import("@tiptap/extension-table-header"),
    import("@tiptap/extension-table-cell"),
  ]).then(([
    core,
    starterKit,
    link,
    heading,
    figureImage,
    callout,
    columnExtension,
    listItem,
    orderedList,
    bulletList,
    textAlign,
    table,
    tableRow,
    tableHeader,
    tableCell,
  ]) => ({
    Editor: core.Editor,
    StarterKit: starterKit.default,
    Link: link.default,
    Heading: heading.default,
    FigureImage: figureImage.FigureImage,
    Callout: callout.default,
    ColumnExtension: columnExtension.ColumnExtension,
    ListItem: listItem.default,
    OrderedList: orderedList.default,
    BulletList: bulletList.default,
    TextAlign: textAlign.default,
    Table: table.default,
    TableRow: tableRow.default,
    TableHeader: tableHeader.default,
    TableCell: tableCell.default,
  }))

  return editorModulesPromise
}

function migrateNodes(nodes) {
  if (!nodes) return nodes
  return nodes.map(node => {
    if (node.type === 'uploadImage' || node.type === 'image') {
      return {
        type: 'figureImage',
        attrs: {
          src: node.attrs.src,
          alt: node.attrs.alt,
          title: node.attrs.title || null,
          float: null,
          size: 'full',
        },
      }
    }
    if (node.content) {
      return { ...node, content: migrateNodes(node.content) }
    }
    return node
  })
}

export default class extends Controller {
  static targets = ["editor", "jsonInput", "htmlInput"];
  static values = { json: String };

  async connect() {
    this._setToolbarEnabled(false)

    const {
      Editor,
      StarterKit,
      Link,
      Heading,
      FigureImage,
      Callout,
      ColumnExtension,
      ListItem,
      OrderedList,
      BulletList,
      TextAlign,
      Table,
      TableRow,
      TableHeader,
      TableCell,
    } = await loadEditorModules()

    if (!this.element.isConnected) {
      return
    }

    const savedJson = this.element.dataset.tiptapJson
    const savedHtml = this.element.dataset.tiptapHtml

    this.jsonInputTarget.value = savedJson;
    this.htmlInputTarget.value = savedHtml;

    let content = savedJson ? JSON.parse(savedJson) : ''
    if (content && content.content) {
      content.content = migrateNodes(content.content)
    }

    this.editor = new Editor({
      element: this.editorTarget,
      content,
      extensions: [
        StarterKit,
        FigureImage,
        Callout,
        ColumnExtension,
        Link.configure({
          openOnClick: true,
        }),
        Heading.configure({
          levels: [1, 2, 3],
        }),
        BulletList,
        OrderedList,
        ListItem,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        Table.configure({ resizable: true }),
        TableRow,
        TableHeader,
        TableCell,
      ],
      onUpdate: ({ editor }) => {
        this.jsonInputTarget.value = JSON.stringify(editor.getJSON());
        this.htmlInputTarget.value = editor.getHTML();
      },
    });

    this.editor.on("image-upload", ({file}) => {
      const formData = new FormData()
      formData.append('file', file)

      api.post('/images', formData)
        .then(data => {
          this.editor.commands.insertFigure({ src: data.url })
        })
        .catch(error => console.error('Image upload failed:', error))
    });

    this._setToolbarEnabled(true)
  }

  _toolbarButtons() {
    return this.element.querySelectorAll('button[data-action*="tiptap#"]')
  }

  _setToolbarEnabled(enabled) {
    this._toolbarButtons().forEach(btn => {
      btn.disabled = !enabled
      btn.setAttribute('aria-busy', enabled ? 'false' : 'true')
    })
  }

  // Defensive guard: toolbar buttons are disabled until ready, but skip the
  // prompt if a programmatic call slips through before the editor mounts.
  _run(fn) {
    if (this.editor) fn(this.editor)
  }

  addLink() {
    if (!this.editor) return;
    const url = prompt("Enter the URL:");
    if (!url) return;
    this._run(e => e.chain().focus().setLink({ href: url }).run())
  }

  toggleBulletList()   { this._run(e => e.chain().focus().toggleBulletList().run()) }
  toggleOrderedList()  { this._run(e => e.chain().focus().toggleOrderedList().run()) }

  removeColumns()      { this._run(e => e.chain().focus().unsetColumns().run()) }
  _setColumns(n)       { this._run(e => e.chain().focus().setColumns(n).run()) }
  setTwoColumns()      { this._setColumns(2) }
  setThreeColumns()    { this._setColumns(3) }

  alignLeft()          { this._run(e => e.commands.setTextAlign('left')) }
  alignCenter()        { this._run(e => e.commands.setTextAlign('center')) }
  alignRight()         { this._run(e => e.commands.setTextAlign('right')) }
  justify()            { this._run(e => e.commands.setTextAlign('justify')) }

  setHeading(event) {
    const level = parseInt(event.currentTarget.dataset.headingLevel, 10);
    this._run(e => e.chain().focus().toggleHeading({ level }).run())
  }

  // Image size controls
  setImageSmall()  { this._run(e => e.commands.setFigureSize('small')) }
  setImageMedium() { this._run(e => e.commands.setFigureSize('medium')) }
  setImageLarge()  { this._run(e => e.commands.setFigureSize('large')) }
  setImageFull()   { this._run(e => e.commands.setFigureSize('full')) }

  // Image float controls
  setImageFloatLeft()  { this._run(e => e.commands.setFigureFloat('left')) }
  setImageCenter()     { this._run(e => e.commands.setFigureFloat(null)) }
  setImageFloatRight() { this._run(e => e.commands.setFigureFloat('right')) }

  // Callout controls
  _setOrChangeCallout(type) {
    this._run(e => {
      const { $from } = e.state.selection
      let insideCallout = false
      for (let d = $from.depth; d > 0; d--) {
        if ($from.node(d).type.name === 'callout') {
          insideCallout = true
          break
        }
      }
      if (insideCallout) {
        e.commands.setCalloutType(type)
      } else {
        e.commands.setCallout({ type })
      }
    })
  }

  setCalloutInfo()    { this._setOrChangeCallout('info') }
  setCalloutNote()    { this._setOrChangeCallout('note') }
  setCalloutWarning() { this._setOrChangeCallout('warning') }
  removeCallout()     { this._run(e => e.chain().focus().removeCallout().run()) }

  // Table controls
  insertTable()     { this._run(e => e.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()) }
  addRowAfter()     { this._run(e => e.chain().focus().addRowAfter().run()) }
  addColumnAfter()  { this._run(e => e.chain().focus().addColumnAfter().run()) }
  deleteRow()       { this._run(e => e.chain().focus().deleteRow().run()) }
  deleteColumn()    { this._run(e => e.chain().focus().deleteColumn().run()) }
  toggleHeaderRow() { this._run(e => e.chain().focus().toggleHeaderRow().run()) }
  deleteTable()     { this._run(e => e.chain().focus().deleteTable().run()) }

  // Divider
  setHorizontalRule() { this._run(e => e.chain().focus().setHorizontalRule().run()) }

  disconnect() {
    if (this.editor) {
      this.editor.destroy();
    }
  }
}
